import { Injectable, inject } from '@angular/core';
import { FeedsUtilityService } from './helper/feeds-utility.service';
import { AccountSettingsService } from '@shared/service/http_services/accountSetting.service';
import { BehaviorSubject, combineLatest, filter, forkJoin, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { StatisticsAggrigatorService } from '@shared/service/http_services/statistics-aggrigator.service';
import { SidenavMenuGenerator } from './helper';
import { ESidenavMenuType } from './helper/sidenav-menu-type.enum';
import { ESlidenavMenu, ISidenavMenu } from './components/sidenav-menu-item/models';
import { InvokeSignalService } from '@shared/service/signal_services/invoke-signal.service';
import { ActivitiesAggrigatorService } from '@shared/service/http_services/activitiesAggrigator.service';
import { BooklistAggrigatorService } from '../booklists/services/booklist-aggrigator.service';
import { ClubAggregatorService } from '../clubs/services/club-aggregator.service';
import { PostAggService } from '../posts/services/postAggrigator.service';
import { QuoteAggrigatorService } from '../quotes/services/quote-aggrigator.service';
import { ReviewsAggrigatorService } from '../reviews/services/reviewsAggrigator.service';
import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { ActivityPluseAggrigatorModel } from '@shared/model/activity-plus-aggrigator-model';
import { Router } from '@angular/router';
import { htmlUrls } from '@shared/utils/router-paths';

import { FeedUsersModel } from '@shared/model/feed-users-model';
import { LocalRepository } from '@shared/helper/local-repository';
import { BookmarkHandlerService, CreateBookmarkModel } from '@shared/service/http_services/handler/bookmark-handler.service';
import { CreateLikeModel } from '@shared/model/customerModels/like-model';
import { LikesService } from '@shared/service/http_services/like.service';
import { RestrictionMessageDialogComponent } from '@shared/components/dialogs/restriction-message-dialog/restriction-message-dialog/restriction-message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Util } from '@shared/utils/utils';
import { FilterOptionsEnum } from '@shared/model/enum/filter-options-enum';
import { ITagContent, SEOService } from '@shared/service/other_services/seo.service';
import { environment } from '@environments/environment';
import { Title } from '@angular/platform-browser';
import { ServiceResult } from '@shared/helper/service-result';
import { TServiceResult } from '@shared/helper/t-service-result';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { FEED_SCROLL_POSITION_KEY } from 'constants/storage-keys';
import { ViewTrackerService } from '@shared/service/view-service/view-tracker.service';
import { StatisticsService } from '@shared/service/http_services/statistics.service';
import { BannerService } from '@shared/service/http_services/banner.service';
import { BannerModel } from '@shared/model/banner-model';
import { SnackbarService } from '@shared/utils/snackbar.service';
import { SnackBarTypes } from '@shared/components/cutome-snack-bar/custome-snack-bar.component';
import { ChallengeAchievementService } from '../challenges/services/challenge-achievement.service';
import { ChallengeStateEnum } from '../challenges/enum/challenge-state.enum';
import { ChallengeAchievementModel } from '../challenges/models/challenge-achievement-model';
import { UserStateService } from '@app/state/user-state.service';
import { User } from '@shared/model/user-context/user.model';
declare let Goftino: any;
@Injectable({
  providedIn: 'root'
})
export class FeedsFacadeService {
  private feedsUtilityService = inject(FeedsUtilityService);
  private accountSettingsService = inject(AccountSettingsService);
  private statisticsAggrigatorService = inject(StatisticsAggrigatorService);
  private sidenavMenuGenerator = inject(SidenavMenuGenerator);
  private invokeSignalService = inject(InvokeSignalService);
  private activitiesAggrigatorService = inject(ActivitiesAggrigatorService);
  private postAggService = inject(PostAggService);
  private clubAggregatorService = inject(ClubAggregatorService);
  private quoteAggrigatorService = inject(QuoteAggrigatorService);
  private reviewsAggrigatorService = inject(ReviewsAggrigatorService);
  private bookListAggrigatorService = inject(BooklistAggrigatorService);
  private bookmarkHandlerService = inject(BookmarkHandlerService);
  private likesService = inject(LikesService);
  private router = inject(Router);
  private repo = inject(LocalRepository);
  private _dialog = inject(MatDialog);
  private utils = inject(Util);
  private seoService = inject(SEOService);
  private title = inject(Title);
  private viewTrackerService = inject(ViewTrackerService);
  private statisticsService = inject(StatisticsService);
  private bannerService = inject(BannerService);
  private snackbarService = inject(SnackbarService);
  private challengeService = inject(ChallengeAchievementService);
  private userStateService = inject(UserStateService);


  private _activities$: BehaviorSubject<ActivityPluseAggrigatorModel[]> = new BehaviorSubject<ActivityPluseAggrigatorModel[]>([]);
  private _banners$: BehaviorSubject<BannerModel[]> = new BehaviorSubject<BannerModel[]>([]);
  private _users$: BehaviorSubject<FeedUsersModel[]> = new BehaviorSubject<FeedUsersModel[]>([]);
  private _newFeedsAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _onTransitionDone$: Subject<void> = new Subject<void>();
  private suggestedList: ActivityPluseAggrigatorModel[] = [];
  private _activeChallenge$ = new BehaviorSubject<ChallengeAchievementModel | null>(null);


  public get onTransitionDone$(): Observable<void> {
    return this._onTransitionDone$.asObservable();
  }

  public get activities$(): Observable<ActivityPluseAggrigatorModel[]> {
    return this._activities$.asObservable();
  }
  public set activities$(value: ActivityPluseAggrigatorModel[]) {
    this._activities$.next(value);
  }
  public get users$(): Observable<FeedUsersModel[]> {
    return this._users$.asObservable();
  }
  public set users$(value: FeedUsersModel[]) {
    this._users$.next(value);
  }
  public get newFeedsAvailable$() {
    return this._newFeedsAvailable$.asObservable();
  }

  public get viewTracker() {
    return this.viewTrackerService;
  }

  public get isInMobile() {
    return this.repo.IsInMobile;
  }

  public get banners$(): Observable<BannerModel[]> {
    return this._banners$.asObservable();
  }

  public get activeChallenge$(): Observable<ChallengeAchievementModel | null> {
    return this._activeChallenge$.asObservable();
  }


  ignoredFeeds: number[] = [];
  hasMoreFeeds!: boolean;
  firstFeedDate!: string;
  lastFeedDate!: string;
  hasMoreUsers = false;
  lastUserFeedsDate!: string;
  activeUserId!: string;
  activeUserIndex = -1;
  loadingUsers = false;
  nextUser!: FeedUsersModel;
  activitySearchFilter: SearchFilterModel = new SearchFilterModel(0, 20);
  searchFilterPickedChallenges: SearchFilterModel = new SearchFilterModel();
  loadingNewUsers = false;
  changingUser = false;
  loadingFeeds = false;
  loadingMoreFeeds = false;
  loadingNewFeeds = false;
  recivedNewFeeds = false;
  _loadingBanner = true;
  loadingChallenge = false;



  constructor() {
    const repo = this.repo;
    if (repo.IsInBrowser) {
      this.handleNewFeedsSignalR().subscribe();
    }
  }

  public setUsersVisibility(): boolean {
    return this.feedsUtilityService.setUsersVisibility();
  }

  public getAccountSettings(): Observable<number[]> {
    // ? return cached data in memory(subject)
    if (this._activities$.value.length > 0) {
      return of([]);
    }
    return this.accountSettingsService.getSetting().pipe(
      map(res => {
        if (res.hasError) return [];
        else return res?.result?.ignoreFeedVerbConfigs || [];
      }),
      tap(ignoredVerbs => {
        this.ignoredFeeds = ignoredVerbs;
        if (this.ignoredFeeds && this.ignoredFeeds?.length > 0) {
          this.activitySearchFilter.SetFilter({
            operation: FilterOptionsEnum.NotIn,
            propertyName: 'verb',
            value: this.ignoredFeeds
          });
        }
      })
    );
  }

  public buildSidenavItems(): Observable<ISidenavMenu[] | null> {
    return combineLatest({
      statistics: this.statisticsAggrigatorService.getMyProfileStatistics(),
      userInfo: this.userStateService.userData,
    }).pipe(
      map(res => {
        if (res.statistics.error || res.statistics.hasError) return null;
        else {
          return this.sidenavMenuGenerator.generate(ESidenavMenuType.MOBILE_FEEDS);
        }
      })
    );
  }

  // ? check the functionality
  public handleMessageCount(): Observable<any> {
    return this.invokeSignalService.getMessages().pipe(
      filter(res => res && res?.data?.payLoad?.type === 'InsertChat')
    );
  }

  public handleNewFeedsSignalR(): Observable<any> {
    return this.invokeSignalService.getFeeds().pipe(
      filter(res => res && (res?.data?.payLoad?.type == 'Update') && (!this.ignoredFeeds?.some(() => !!res.data.payLoad.data.verb))),
      tap(() => this._newFeedsAvailable$.next(true))
    );
  }


  public loadInitialPageData(userId: string): Observable<any> {
    this.loadingFeeds = true;

    // ? return cached data in memory(subject)
    if (this._activities$.value.length > 0) {
      this.changingUser = false;
      this.loadingFeeds = false;
      this.loadingChallenge = false;
      return of(null);
    }

    const latestReviewsTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    const bookListTermFilter: TermFilterModel = new TermFilterModel(0, 15);
    const quoteTermFilter: TermFilterModel = new TermFilterModel(0, 15);
    const latestPostTermFilter: TermFilterModel = new TermFilterModel(0, 15);
    const latestClubsSearchFilter: SearchFilterModel = new SearchFilterModel();
    this._activities$.next([]);

    return forkJoin({
      feeds: this.activitiesAggrigatorService.getMyAggregateFeedsByDateV2('', this.activitySearchFilter),
      suggestedReviews: this.reviewsAggrigatorService.getChosenReviewsWithUserLiked(latestReviewsTermfilter),
      suggestedBooklists: this.bookListAggrigatorService.getChosenBookListsWithUserLiked(bookListTermFilter),
      suggestedQoutes: this.quoteAggrigatorService.getChosenQuotesWithUserLiked(quoteTermFilter),
      suggestedPosts: this.postAggService.getChosenBookListsWithUserLiked(latestPostTermFilter),
      suggestedClubs: this.clubAggregatorService.getChosenClubsWithState(latestClubsSearchFilter),
      activeChallenge: this.getChallengeData(userId)
    }).pipe(
      tap(data => {
        const filteredActivities = this.feedsUtilityService.checkAndfilterActivities(data?.feeds?.result?.items);
        this.hasMoreFeeds = data?.feeds?.result?.hasNextPage;
        this.firstFeedDate = data?.feeds?.result?.items[0]?.mainFeed?.createdAt || new Date().toString(); //? think about it
        this.lastFeedDate = data?.feeds?.result?.items[data?.feeds?.result?.items?.length - 1]?.mainFeed?.createdAt || new Date().toString(); //? think about it
        const adaptedBookList = this.feedsUtilityService.AdaptBookListToFeedModel(data.suggestedBooklists?.result?.items) || [];
        const adaptedQuotes = this.feedsUtilityService.AdaptQuotesToFeedModel(data.suggestedQoutes?.result?.items) || [];
        const adaptedReviews = this.feedsUtilityService.AdaptReviewsToFeedModel(data.suggestedReviews?.result?.items) || [];
        const adaptedPosts = this.feedsUtilityService.AdaptPostsToFeedModel(data.suggestedPosts?.result?.items) || [];
        const adaptedClubs = this.feedsUtilityService.AdaptClubsToFeedModel(data.suggestedClubs?.result?.items) || [];
        this.suggestedList = this.feedsUtilityService.randomizeSuggestions([...adaptedBookList, ...adaptedQuotes, ...adaptedReviews, ...adaptedPosts, ...adaptedClubs]);
        this.changingUser = false;
        this.loadingFeeds = false;
        this.combineFeedsWithSuggestions(filteredActivities, false);
      }),
      switchMap(() => (this._banners$.value.length < 1) ? this.getBanners() : of(this._banners$.value))
    );
  }

  private combineFeedsWithSuggestions(activities: ActivityPluseAggrigatorModel[], loadMore?: boolean): void {
    const itemsToAddFromSecondArray = 3;
    const activityList = this._activities$.value;

    for (let i = loadMore ? 2 : 0; i < activities?.length || (this.hasMoreFeeds ? false : this.suggestedList?.length > 0); i++) {
      if (i < activities?.length) {
        activityList.push(activities[i]);
      }
      if ((i + 1) % itemsToAddFromSecondArray === 0 && this.suggestedList?.length > 0) {
        activityList.push(this.suggestedList.shift()!);
      }
    }
    if (this.suggestedList.length > 0 && !this.hasMoreFeeds) {
      activityList.concat(this.suggestedList);
    }
    this._activities$.next(activityList);
  }

  // ? only useful for Goftino
  public navigateToMenuItem(item: ISidenavMenu): void {
    if (item.type === ESlidenavMenu.SUPPORT)
      Goftino.open();
  }


  public loadUsers(): Observable<TServiceResult<PageList<FeedUsersModel>>> {
    this.loadingUsers = true;

    if (this._users$.value.length > 0) {
      this.loadingUsers = false;
      return of({
        hasError: false,
        result: {
          items: this._users$.value,
          hasNextPage: this.hasMoreUsers
        }
      } as TServiceResult<PageList<FeedUsersModel>>);
    }

    return this.activitiesAggrigatorService.getAggregatedFeedUser(this.activitySearchFilter).pipe(
      tap(feedUsers => {
        if (!feedUsers.hasError && feedUsers?.result?.items?.length > 0) {
          this._users$.next(feedUsers?.result?.items || []);
          this.hasMoreUsers = feedUsers.result.hasNextPage || false;
          this.lastUserFeedsDate = new Date(feedUsers?.result?.items[feedUsers?.result?.items.length - 1]?.createdAt!)?.toISOString() || new Date().toISOString();

          if (this.activeUserId?.length > 1) {
            this.activeUserIndex = this._users$.value.findIndex(x => x.actorId === this.activeUserId);
          }
          this.setNextUser(this._users$.value[this.activeUserIndex]?.actor?.id || '');
        }
        this.loadingUsers = false;
      })
    );
  }



  public loadMoreUsers(): Observable<TServiceResult<PageList<FeedUsersModel>>> {

    this.loadingNewUsers = true;
    const formattedDate = new Date(this.lastUserFeedsDate).toISOString();

    return this.activitiesAggrigatorService.getAggregatedFeedUser(this.activitySearchFilter, false, formattedDate).pipe(
      tap(newUsers => {
        this.loadingNewUsers = false;

        if (newUsers.hasError || !newUsers?.result?.items?.length) {
          return;
        }

        const items = newUsers.result.items;
        const currentUsers = this._users$.value;
        const newUniqueItems = items.filter(item => !currentUsers.some(u => u.actorId === item.actorId));
        if (newUniqueItems.length === 0) {
          return;
        }

        this._users$.next([...currentUsers, ...newUniqueItems]);
        this.hasMoreUsers = newUsers.result.hasNextPage || false;

        const lastCreatedAt = newUniqueItems[newUniqueItems.length - 1]?.createdAt;
        if (lastCreatedAt) {
          this.lastUserFeedsDate = new Date(lastCreatedAt).toISOString();
        }
      })
    );
  }



  public loadUserFeeds(userId: string): Observable<any> {
    this.changingUser = true;
    this.setActiveUserIndex(userId);
    // ? when all users button clicked
    if (!userId) {
      this._activities$.next([]);
      return this.loadInitialPageData(userId!);
    }
    else {
      this.loadingFeeds = true;
      this.setActiveUserIndex(userId);
      return this.activitiesAggrigatorService.getMyAggregateFeedsByDateV2(userId, this.activitySearchFilter)
        .pipe(
          tap(data => {
            if (!data.hasError && data.result) {
              this._activities$.next([]);
              const filteredActivities = this.feedsUtilityService.checkAndfilterActivities(data?.result.items);
              this._activities$.next(filteredActivities);
              this.hasMoreFeeds = data.result?.hasNextPage;
              this.firstFeedDate = this._activities$.value[0]?.mainFeed?.createdAt || new Date().toString();
              this.lastFeedDate = this._activities$.value[this._activities$.value?.length - 1]?.mainFeed?.createdAt || new Date().toString();
              this.changingUser = false;
              this.loadingFeeds = false;
              this.setNextUser(userId);
            }
          })
        );
    }
  }

  public loadMoreUserFeeds(): Observable<TServiceResult<PageList<ActivityPluseAggrigatorModel>> | null> {
    if (this.hasMoreFeeds) {
      this.loadingMoreFeeds = true;
      return this.activitiesAggrigatorService.getMyAggregateFeedsByDateV2(this.activeUserId, this.activitySearchFilter, false, this.lastFeedDate).pipe(
        tap(res => {
          if (!res.hasError) {
            this.lastFeedDate = res?.result?.items[res?.result?.items?.length - 1]?.mainFeed?.createdAt || new Date().toString();
            this.hasMoreFeeds = res?.result?.hasNextPage;
            if (this.activeUserId) {
              this._activities$.next([...this._activities$.value, ...res?.result?.items])
            }
            else {
              this.combineFeedsWithSuggestions(res?.result?.items, true);
            }
          }
          this.loadingMoreFeeds = false;
        })
      );
    }
    else
      return of(null);
  }


  public getNewFeeds(): Observable<TServiceResult<PageList<ActivityPluseAggrigatorModel>> | null> {
    if (this.repo.IsInBrowser) {
      this._newFeedsAvailable$.next(false);
      this.loadingNewFeeds = true;
      return this.activitiesAggrigatorService.getMyAggregateFeedsByDateV2(this.activeUserId, this.activitySearchFilter, true, this.firstFeedDate).pipe(
        tap(res => {
          if (!res.hasError) {
            this._activities$.next([...res?.result?.items, ...this._activities$.value])
            this.firstFeedDate = res?.result?.items[0]?.mainFeed?.createdAt || new Date().toString();
          }
          this.loadingNewFeeds = false;
        })
      );
    }
    else return of(null);
  }


  private setNextUser(activeUserId?: string): void {
    const currentUserIndex = this._users$.value.findIndex(x => x?.actor?.id === activeUserId);
    this.nextUser = this._users$.value[currentUserIndex + 1];
  }

  private setActiveUserIndex(userId: string): void {
    this.activeUserId = userId;
    this.activeUserIndex = userId?.length > 1 ? this._users$.value.findIndex(x => x.actorId === userId) : -1;
  }

  public bookmarkItem(bookmarkData: CreateBookmarkModel): Observable<ServiceResult> {
    return this.bookmarkHandlerService.handleBookmark(bookmarkData).pipe(
      tap(res => {
        if (!res.hasError) {
          bookmarkData.object.userMarked = bookmarkData.userMarked;
        }
      })
    );
  }


  public openBannerTab(hashedLink: string): Observable<any> {
    if (this.repo.IsInBrowser) {
      return this.statisticsService.redirectToLink(hashedLink).pipe(
        tap(res => {
          if (res.hasError) {
            this.snackbarService.openSnackbar(SnackBarTypes.error, 'عملیات با خطا مواجه شد');
            return;
          }
          const url = res.result;
          window.open(url, '_blank');
        })
      );
    }
    return of(null);
  }

  private getBanners() {
    const bannerTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    this._loadingBanner = true;
    return this.bannerService.getBanners(bannerTermfilter).pipe(
      tap(res => {
        this._loadingBanner = false;
        if (res.hasError) {
          this._banners$.next([]);
        }
        this._banners$.next(res.result.items);
      })
    );
  }

  public likeItem(likedObject: CreateLikeModel, currentUser: User | null): Observable<ServiceResult | null> {
    if (!currentUser?.uniqeName && !currentUser?.firstName) {
      this.utils.opensigninDialog(this._dialog, '/', this.repo);
      return of(null);
    }
    return this.likesService.createLike(likedObject, currentUser.id || "").pipe(
      tap(res => {
        if (res?.hasError) {
          likedObject.userLiked = false;
          this._dialog.open(RestrictionMessageDialogComponent, {
            panelClass: 'restrict-message-dialog',
            data: likedObject
          })
        }
        else {
          const tempActivities = [...this._activities$.value];
          const target = tempActivities.find(a => a?.mainFeed?.inDirectObject?.objectId === likedObject?.objectId);
          if (likedObject.userLiked) {
            target!.mainFeed.userLiked = false;
            target!.mainFeed.totalLikes!--;
          }
          else {
            target!.mainFeed.userLiked = true;
            target!.mainFeed.totalLikes!++;
          }
          this._activities$.next([...tempActivities]);
        }
      })
    );
  }

  public findFriends(): void {
    this.router.navigate([htmlUrls.setting_profile_friends]);
  }

  public updateMetaTags(): void {
    const seoContent: ITagContent = {
      robots: 'index',
      url: environment.SiteUrl,
      title: 'بهخوان، شبکه اجتماعی کتاب‌دوستان',
      image: 'https://behkhaan.ir/assets/icons/icon-144x144.png',
    }
    this.seoService.updateTags(seoContent);
    this.title.setTitle('بهخوان، شبکه اجتماعی کتاب‌دوستان');
  }


  public setScrollPosition(position: number): void {
    if (this.repo.IsInBrowser) {
      this.repo.setLocalItem(FEED_SCROLL_POSITION_KEY, position.toString());
    }
  }

  public getScrollPosition(): number | null {
    if (this.repo.IsInBrowser) {
      return parseInt(this.repo.getLocalItem(FEED_SCROLL_POSITION_KEY)!, 10) ?? null;
    }
    else return null;
  }

  public getChallengeData(userId: string): Observable<any> {
    this.loadingChallenge = true;
    const state: ChallengeStateEnum[] = [ChallengeStateEnum.Started];

    if (userId) {
      return this.challengeService.getChallengesByFilter(this.searchFilterPickedChallenges, state, userId).pipe(
        tap(challengesList => {
          if (!challengesList.hasError) {
            const challenges = challengesList?.result?.items;
            if (challenges.length > 0) {
              const latestChallenge = challenges.sort((a, b) => b.level! - a.level!)[0];
              this._activeChallenge$.next(latestChallenge);
            }
          }
          this.loadingChallenge = false;
        })
      );
    } else {
      return of(null)
    }
  }

  public clearFeedCache() {
    this._activities$.next([]);
    this._users$.next([]);
    this._activeChallenge$.next(null);
  }

  public runSetTimeout() {
    this._onTransitionDone$.next();
  }

}
